import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getHeaders } from '../utils/headers.functions';

@Injectable({ providedIn: 'root' })
export class BuilderApiService {
  private base = environment.API_URL;

  constructor(private http: HttpClient) {}

  async getProductIdBySlug(slug: string): Promise<number> {
    const url = `${this.base}/product-slug/${encodeURIComponent(slug)}`;
    const resp = await firstValueFrom(this.http.get<any>(url));
    return resp?.ProductId ?? resp?.productId ?? resp?.id ?? null;
  }

  async uploadFile(file: File | Blob, type: string = 'landing'): Promise<string> {
    const fd = new FormData();
    fd.append('file', file);
    if (type) fd.append('type', type);
    const headers: HttpHeaders = getHeaders();
    const resp: any = await firstValueFrom(this.http.post(`${this.base}/uploadfile`, fd, { headers }));
    
    // Backend retorna path del archivo (ej: "landing/archivo.png")
    // Retornamos el path tal cual para almacenarlo en el componente
    const path = resp?.path || resp?.filename;
    return path || '';
  }

  isHttpUrl(v: any): boolean {
    return typeof v === 'string' && /^https?:\/\//i.test(v);
    }

  isEmbedded(v: any): boolean {
    return typeof v === 'string' && /^(data:|blob:)/i.test(v);
  }

  async dataUrlToFile(dataUrl: string, name: string = 'asset.png'): Promise<File> {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type || 'application/octet-stream' });
  }

  private async fixUrl(value: any, fallbackName: string): Promise<string | null> {
    if (typeof value !== 'string' || !value) return null;
    if (this.isHttpUrl(value)) return value;
    // Si es un path relativo (ej: "landing/file.png"), convertir a URL completa
    if (/^[a-z0-9_-]+\/[a-z0-9_.-]+$/i.test(value)) {
      const [type, filename] = value.split('/');
      return `${this.base}/file/${type}/${filename}`;
    }
    if (this.isEmbedded(value)) {
      try {
        const file = await this.dataUrlToFile(value, fallbackName);
        const path = await this.uploadFile(file, 'landing');
        if (!path) return null;
        // Convertir el path a URL completa
        const [type, filename] = path.includes('/') ? path.split('/') : ['landing', path];
        return `${this.base}/file/${type}/${filename}`;
      } catch { return null; }
    }
    return null;
  }

  async sanitizeBuilder(builder: any): Promise<any> {
    const clone = JSON.parse(JSON.stringify(builder));

    const sanitizeNode = async (node: any): Promise<void> => {
      if (!node || typeof node !== 'object') return;

      const props = node.props || node;
      if (props) {
        if (typeof props.src === 'string') {
          const fixed = await this.fixUrl(props.src, 'asset.png');
          if (fixed) props.src = fixed;
        }
        if (typeof props.avatarUrl === 'string') {
          const fixed = await this.fixUrl(props.avatarUrl, 'avatar.png');
          if (fixed) props.avatarUrl = fixed;
        }
      }

      // Estructura específica de fila
      if (node.tipo === 'fila' && node.props && Array.isArray(node.props.cols)) {
        for (const col of node.props.cols) {
          if (Array.isArray(col.children)) {
            for (const child of col.children) {
              await sanitizeNode(child);
            }
          }
        }
      }

      // Recorre cualquier objeto/array adicional por robustez
      for (const key of Object.keys(node)) {
        const v = node[key];
        if (Array.isArray(v)) {
          for (const item of v) await sanitizeNode(item);
        } else if (v && typeof v === 'object') {
          await sanitizeNode(v);
        }
      }
    };

    if (Array.isArray(clone?.layout)) {
      for (const item of clone.layout) {
        await sanitizeNode(item);
      }
    }
    return clone;
  }

  async postBuilder(productId: number, builder: any): Promise<any> {
    const headers: HttpHeaders = getHeaders();
    return await firstValueFrom(this.http.post(`${this.base}/landing-page/builder/${productId}`, builder, { headers }));
  }

  async getBuilder(productId: number): Promise<any> {
    const headers: HttpHeaders = getHeaders();
    return await firstValueFrom(this.http.get(`${this.base}/landing-page/builder/${productId}`, { headers }));
  }

  async prepareAndPostBuilderBySlug(slug: string, builder: any): Promise<any> {
    const productId = await this.getProductIdBySlug(slug);
    if (!productId) throw new Error('No se pudo resolver productId desde slug');
    const sanitized = await this.sanitizeBuilder(builder);
    return await this.postBuilder(productId, sanitized);
  }
}
